/**
 * CartSlotOverlay - Phase 2 of the picking-store/slot split.
 * See PICKING-STORE-AND-SLOT-SPLIT-PLAN.md for the full product context and
 * INTERNAL-DELIVERY-API-BRIEF.md for the Azure API contract.
 *
 * A "Select a time" panel that sits INSIDE the cart drawer (.go-cart-drawer,
 * see snippets/cart-drawer.liquid + snippets/cart-slot-overlay.liquid),
 * layered on top of the cart contents via .is-open. It is the reservation
 * step Phase 1's StorePicker deliberately left alone - StorePicker only ever
 * sets the BROWSING picking store; this overlay is what writes the cart's
 * delivery/reservation attributes.
 *
 * Public API: window.CartSlotOverlay.open(options) / .close() / .prefetch()
 *
 * open(options) accepts:
 *   - preferCurrentReservation: true - seed mode/day/slot from the live
 *     reservation cookies (delivery_type/delivery_date/delivery_unique_window),
 *     applied once the payload for the relevant day/mode has rendered, and
 *     keep this time-edit journey locked to that reservation's mode. Switching
 *     Delivery / Click & Collect belongs to the Edit/change-location journey.
 *   - mode / day / slotId - explicit overrides, same effect as the above but
 *     caller-supplied instead of read from cookies.
 *   - lockMode: true - use the supplied mode without rendering the Delivery /
 *     Click & Collect tabs. A carried StorePicker intent does this automatically.
 *   - returnToCart: true - navigate to the cart after reserving, only for
 *     journeys started by a cart control. Other callers stay on their page.
 *   - withinStorePicker: true - render this same selector inside the open
 *     StorePicker modal instead of the cart drawer / standalone side panel.
 * When the cart drawer (.js-go-cart-drawer) isn't open, the overlay detaches
 * itself from the drawer and renders as a standalone fixed panel (see
 * cart-slot-overlay--standalone in assets/cart-slot-overlay.css) so the cart
 * page's "Edit" link works even with the drawer closed.
 *
 * Depends on globals already defined by earlier deferred scripts (load order
 * in layout/theme.liquid): getCookie/setCookie/storefrontApiURL
 * (liquid-variables.liquid, inline - runs before any deferred script),
 * trapFocus/removeTrapFocus (global.js), window.locationManager
 * (location-manager.js), window.StorePicker (store-picker.js),
 * syncDeliveryAttributesToCart/agileCart (delivery-drawer-functions.js /
 * cart.js - used only to re-use the existing, battle-tested cart-attribute +
 * cart-render pipeline after a reserve, not touched otherwise).
 *
 * Days/times come from ONE Azure payload (storefront/delivery-slots, nested
 * date -> mode -> time[]) and are cascaded client-side into
 * mode -> day -> period -> slot. Reserving posts the chosen slot's `id` as
 * `unique_window` to storefront/reservations.
 */
(function () {
  'use strict';

  var overlay, titleEl, backBtn, closeBtn, fromStoreEl, editBtn,
    modesEl, modeEx, modeM, missingStoreState, openPickerBtn,
    loadingEl, errorEl, basketNoticeEl, daysEl, periodsEl, slotsEl,
    summaryEl, reserveBtn, keepCurrentBtn;

  // In-memory only - re-fetched whenever the picking store/postcode this
  // payload was fetched for no longer matches the current ones (see open()).
  var state = {
    payload: null,
    fetchedFor: null, // { store, postcode }
    inFlight: null,   // { store, postcode } while a request is outstanding
    mode: null,       // 'EX' | 'M'
    day: null,        // 'YYYY-MM-DD' key into payload.slots
    period: null,     // 'morning' | 'afternoon' | 'evening' | null (ungrouped)
    slot: null,       // the selected { description, value, id } from payload
    source: null,     // 'store-picker' | 'cart-drawer' | 'standalone'
    returnToCart: false, // explicit cart-entry intent, independent of presentation
    lockedMode: null, // 'EX' | 'M' when StorePicker already established intent
    context: null,    // candidate { store, postcode, suburb, name, shoppingIntent }
    commitContext: null,
    preserveCurrentReservation: false,
    previousReservation: null,
    equivalentTimeMatched: false,
    reservationPending: false,
    reservationEpoch: 0,
    reservationOperation: null,
    recovery: null,
    fetchEpoch: 0,
    // Pending { mode, day, slotId, timeShort } requested by open(options),
    // consumed piecemeal as the payload cascades (mode in renderAfterPayload,
    // day in renderDays, period in renderPeriods, slot at the end of
    // renderSlots - see resolvePrefill()/findPrefillSlot()/applyPrefillSelection()).
    prefill: null
  };

  // True once the overlay has been detached from the cart drawer and
  // rendered as a standalone fixed panel (drawer closed / cart page).
  var standaloneActive = false;
  var standaloneHome = null; // { parent, nextSibling } to restore on exit
  var storePickerActive = false;
  var storePickerHome = null; // { parent, nextSibling } to restore on exit
  var storePickerPanel = null;
  var suspendedStorePickerModal = null;
  var SLOT_MODE_INTENT_KEY = 'hf_slot_mode_intent';
  var RESERVATION_RECOVERY_KEY = 'hf_slot_reservation_pending';
  var REQUEST_TIMEOUT_MS = 15000;
  var CART_UPDATE_TIMEOUT_MS = 60000;
  var pendingCartUpdate = null;
  var opener = null;
  var storePickerA11y = null;
  var recoveryPersistenceFailed = false;

  // Abort limits the browser wait, not the server-side reservation. A timed-out
  // write is therefore unresolved until a read confirms its actual outcome.
  function requestJSON(url, options, onLateResult) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var settings = Object.assign({ cache: 'no-store', headers: { Accept: 'application/json' } }, options || {});
    if (controller) settings.signal = controller.signal;
    var cancel;
    var promise = new Promise(function (resolve, reject) {
      var settled = false;
      var timer;
      cancel = function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (controller) controller.abort();
        reject(new Error('Request timed out or was cancelled'));
      };
      timer = setTimeout(cancel, REQUEST_TIMEOUT_MS);
      Promise.resolve().then(function () { return fetch(url, settings); })
        .then(function (response) {
          if (!response.ok) throw new Error('Request responded ' + response.status);
          return response.json();
        }).then(function (data) {
          if (settled) {
            if (onLateResult) onLateResult(data);
            return;
          }
          settled = true;
          clearTimeout(timer);
          resolve(data);
        }, function (error) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(error);
        });
    });
    promise.cancel = function () { if (cancel) cancel(); };
    return promise;
  }

  function invalidateSlotRequest() {
    state.fetchEpoch++;
    if (state.inFlight && state.inFlight.cancel) state.inFlight.cancel();
    state.inFlight = null;
    showLoading(false);
  }

  function readReservationRecovery() {
    // A cart identity change must not hide an unresolved write. The recovery
    // path validates the current cart separately before using any old result.
    if (!recoveryPersistenceFailed) {
      try {
        var saved = JSON.parse(localStorage.getItem(RESERVATION_RECOVERY_KEY) || 'null');
        return saved && saved.cartId && saved.slot && saved.slot.id ? saved : null;
      } catch (e) { /* in-memory recovery still guards this page */ }
    }
    return state.recovery;
  }

  function saveReservationRecovery(recovery) {
    state.recovery = recovery;
    try {
      if (recovery) localStorage.setItem(RESERVATION_RECOVERY_KEY, JSON.stringify(recovery));
      else localStorage.removeItem(RESERVATION_RECOVERY_KEY);
      recoveryPersistenceFailed = false;
    } catch (e) {
      recoveryPersistenceFailed = true;
      console.warn('CartSlotOverlay: reservation recovery could not be persisted:', e);
    }
  }

  function isReservationUnsettled() {
    return state.reservationPending || !!readReservationRecovery();
  }

  function lockCheckout() {
    if (window.agileCart && typeof window.agileCart.disableCheckout === 'function') {
      window.agileCart.disableCheckout();
    }
  }

  function cacheElements() {
    overlay = document.getElementById('cart-slot-overlay');
    if (!overlay) return false;
    titleEl = overlay.querySelector('.cart-slot-overlay__title');
    backBtn = document.getElementById('cart-slot-back');
    closeBtn = document.getElementById('cart-slot-close');
    fromStoreEl = document.getElementById('cart-slot-store-name');
    editBtn = document.getElementById('cart-slot-edit');
    modesEl = document.getElementById('cart-slot-modes');
    modeEx = document.getElementById('cart-slot-mode-ex');
    modeM = document.getElementById('cart-slot-mode-m');
    missingStoreState = document.getElementById('cart-slot-missing-store');
    openPickerBtn = document.getElementById('cart-slot-open-picker');
    loadingEl = document.getElementById('cart-slot-loading');
    errorEl = document.getElementById('cart-slot-error');
    basketNoticeEl = document.getElementById('cart-slot-basket-notice');
    daysEl = document.getElementById('cart-slot-days');
    periodsEl = document.getElementById('cart-slot-periods');
    slotsEl = document.getElementById('cart-slot-slots');
    summaryEl = document.getElementById('cart-slot-summary');
    reserveBtn = document.getElementById('cart-slot-reserve');
    keepCurrentBtn = document.getElementById('cart-slot-keep-current');
    return true;
  }

  // ---------------------------------------------------------------------
  // Storage readers - mirrors store-picker.js's own copies (kept local
  // rather than shared so this file has no load-order dependency on it).
  // ---------------------------------------------------------------------

  function currentPickingStore() {
    try {
      return localStorage.getItem('currentPickingStore')
        || localStorage.getItem('selectedLocation')
        || (typeof getCookie === 'function' && getCookie('picking_store'))
        || '';
    } catch (e) {
      return (typeof getCookie === 'function' && getCookie('picking_store')) || '';
    }
  }

  function currentPostcode() {
    return (typeof getCookie === 'function' && getCookie('postcode')) || '';
  }

  function currentSuburb() {
    return (typeof getCookie === 'function' && getCookie('suburb')) || '';
  }

  function effectivePickingStore() {
    return (state.context && state.context.store) || currentPickingStore();
  }

  function effectivePostcode() {
    return (state.context && state.context.postcode) || currentPostcode();
  }

  function effectiveSuburb() {
    return (state.context && state.context.suburb) || currentSuburb();
  }

  function modeLabel(mode) {
    return mode === 'M' ? 'Click & Collect' : 'delivery';
  }

  function hasExplicitPickingStore() {
    if (window.StorePicker && typeof window.StorePicker.hasExplicitPickingStore === 'function') {
      return window.StorePicker.hasExplicitPickingStore();
    }
    if (typeof window.hasPickingStore === 'function') return window.hasPickingStore();
    return false;
  }

  function readStoredModeIntent() {
    try {
      var mode = localStorage.getItem(SLOT_MODE_INTENT_KEY);
      return mode === 'EX' || mode === 'M' ? mode : null;
    } catch (e) {
      return null;
    }
  }

  function writeStoredModeIntent(mode) {
    try {
      if (mode === 'EX' || mode === 'M') localStorage.setItem(SLOT_MODE_INTENT_KEY, mode);
      else localStorage.removeItem(SLOT_MODE_INTENT_KEY);
    } catch (e) { /* ignore */ }
  }

  function storeDisplayName(storeNumber) {
    var lm = window.locationManager;
    if (lm && lm.locations && lm.locations[storeNumber]) {
      return lm.locations[storeNumber].name;
    }
    // storeMatrix is defined in first-loaded-scripts.liquid.
    try {
      if (typeof storeMatrix !== 'undefined' && storeMatrix[0]) {
        var fromMatrix = storeMatrix[0][parseInt(storeNumber, 10)];
        if (fromMatrix) return fromMatrix;
      }
    } catch (e) { /* ignore */ }
    return storeNumber ? ('Store ' + storeNumber) : '';
  }

  function formatFromLine(name, suburb, postcode) {
    var line = name || 'your store';
    var extra = suburb || postcode;
    return extra ? (line + ' \u2014 ' + extra) : line;
  }

  // ---------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------

  function open(options) {
    options = options || {};
    if (!overlay && !cacheElements()) return;
    if (state.reservationPending) return;
    state.recoveryCheckFailed = false;
    state.reservationEpoch++;
    if (!overlay.contains(document.activeElement)) opener = document.activeElement;
    var recovery = readReservationRecovery();
    if (recovery) {
      // Reopening an unresolved attempt checks the same cart and intended time.
      // It must not silently start a second reservation for a new selection.
      options = Object.assign({}, options, {
        mode: recovery.mode, day: recovery.day, slotId: recovery.slot.id,
        lockMode: true, preserveCurrentReservation: false,
        context: recovery.context, commitContext: null
      });
    }

    // Reset on every fresh open so a previous cart journey cannot redirect
    // a later header/store journey. Internal continuations pass this explicitly.
    state.returnToCart = options.returnToCart === true;
    state.source = options.withinStorePicker
      ? 'store-picker'
      : (isDrawerOpen() ? 'cart-drawer' : 'standalone');

    state.context = options.context && options.context.store && options.context.postcode
      ? {
          store: String(options.context.store),
          postcode: String(options.context.postcode),
          suburb: String(options.context.suburb || ''),
          name: String(options.context.name || ''),
          shoppingIntent: options.context.shoppingIntent || null
        }
      : null;
    state.commitContext = typeof options.commitContext === 'function' ? options.commitContext : null;

    var currentExpiry = typeof getCookie === 'function'
      ? parseInt(getCookie('delivery_expires_time'), 10)
      : 0;
    state.preserveCurrentReservation = !!(
      options.preserveCurrentReservation && currentExpiry > Date.now()
    );
    state.previousReservation = state.preserveCurrentReservation
      ? {
          mode: getCookie('delivery_type') || null,
          day: getCookie('delivery_date') || null,
          slotId: getCookie('delivery_unique_window') || null,
          timeShort: getCookie('delivery_time_short') || null
        }
      : null;
    state.equivalentTimeMatched = false;

    var carriedMode = options.preferCurrentReservation ? null : readStoredModeIntent();
    var requestedMode = (options.mode === 'EX' || options.mode === 'M')
      ? options.mode
      : carriedMode;
    var prefillOptions = Object.assign({}, options);
    if (!prefillOptions.mode && carriedMode) prefillOptions.mode = carriedMode;
    var prefill = resolvePrefill(prefillOptions);
    state.prefill = prefill;
    var currentReservationMode = options.preferCurrentReservation
      && prefill
      && (prefill.mode === 'EX' || prefill.mode === 'M')
      ? prefill.mode
      : null;
    state.lockedMode = (
      (options.lockMode && requestedMode)
      || carriedMode
      || currentReservationMode
    ) || null;
    // Every fresh journey starts unselected. Editing a live reservation still
    // re-applies its explicit prefill below, but a new journey must not inherit
    // Express / first day / first period from a prior open in this tab.
    state.mode = null;
    state.day = null;
    state.period = null;
    state.slot = null;

    // Never expose controls rendered by a prior open while a new store/mode
    // payload is resolving.
    if (modesEl) modesEl.hidden = true;
    if (modeEx) modeEx.hidden = true;
    if (modeM) modeM.hidden = true;
    if (daysEl) { daysEl.hidden = true; daysEl.innerHTML = ''; }
    if (periodsEl) periodsEl.hidden = true;
    if (slotsEl) slotsEl.innerHTML = '';
    updateFooter();

    if (titleEl) {
      titleEl.textContent = state.lockedMode === 'M'
        ? 'Select a collection time'
        : (state.lockedMode === 'EX' ? 'Select a delivery time' : 'Select a time');
    }

    setPresentationMode(options);

    overlay.hidden = false;
    overlay.classList.add('is-open');
    document.dispatchEvent(new CustomEvent('hfSlotPickerOpened', {
      detail: { source: state.source, mode: state.lockedMode },
      bubbles: true
    }));
    clearError();
    renderFromCardFallback();
    if (recovery) showError(recoveryPrompt(recovery));

    if (!readyToFetch()) {
      invalidateSlotRequest();
      showMissingStore();
      if (typeof trapFocus === 'function') trapFocus(overlay, closeBtn || overlay);
      return;
    }
    hideMissingStore();

    var store = effectivePickingStore();
    var postcode = effectivePostcode();
    if (state.inFlight && (state.inFlight.store !== store || state.inFlight.postcode !== postcode)) {
      invalidateSlotRequest();
    }
    var sameFetch = state.payload && state.fetchedFor
      && state.fetchedFor.store === store && state.fetchedFor.postcode === postcode;

    if (sameFetch) {
      // Reuse this page's previous selector result — paint immediately.
      renderAfterPayload();
    } else if (state.inFlight && state.inFlight.store === store && state.inFlight.postcode === postcode) {
      // A selector request is already running for this store/postcode.
      // Reuse it rather than issuing another request.
      showLoading(true);
    } else {
      state.mode = null;
      state.day = null;
      state.period = null;
      state.slot = null;
      fetchSlots(store, postcode, { quiet: false });
    }

    if (typeof trapFocus === 'function') trapFocus(overlay, closeBtn || overlay);
  }

  // ---------------------------------------------------------------------
  // Prefill (Edit -> reopen with the current reservation pre-selected)
  // ---------------------------------------------------------------------

  function resolvePrefill(options) {
    var fromCookies = null;
    if (options.preferCurrentReservation && typeof getCookie === 'function') {
      var expiresTime = getCookie('delivery_expires_time');
      var hasLiveReservation = expiresTime && parseInt(expiresTime, 10) > new Date().getTime();
      if (hasLiveReservation) {
        fromCookies = {
          mode: getCookie('delivery_type') || null,
          day: getCookie('delivery_date') || null,
          slotId: getCookie('delivery_unique_window') || null,
          timeShort: getCookie('delivery_time_short') || null
        };
      }
    }

    var hasExplicitOverride = !!(options.mode || options.day || options.slotId);
    if (!fromCookies && !hasExplicitOverride) return null;

    return {
      mode: options.mode || (fromCookies && fromCookies.mode) || null,
      day: options.day || (fromCookies && fromCookies.day) || null,
      slotId: options.slotId || (fromCookies && fromCookies.slotId) || null,
      timeShort: (fromCookies && fromCookies.timeShort) || null
    };
  }

  // Looks up the prefill's target slot within the CURRENT mode/day's times
  // (state.mode/state.day must already be set - called from renderPeriods
  // once the day is settled, and again from renderSlots to make the final
  // selection). Matches on id first, falling back to the description text.
  function findPrefillSlot() {
    var prefill = state.prefill;
    if (!prefill) return null;
    var times = currentModeTimes();
    if (prefill.slotId) {
      var byId = times.filter(function (s) { return s.id === prefill.slotId; })[0];
      if (byId) return byId;
    }
    if (prefill.timeShort) {
      var byDesc = times.filter(function (s) { return (s.description || '') === prefill.timeShort; })[0];
      if (byDesc) return byDesc;
    }
    return null;
  }

  // Consumes state.prefill: selects the matching slot button (if the
  // rendered/filtered list actually contains it) and clears the prefill so
  // later manual day/period/slot changes in this session aren't overridden.
  function applyPrefillSelection() {
    if (state.slot || !state.prefill || !slotsEl) return;
    var match = findPrefillSlot();
    if (!match) {
      state.prefill = null;
      return;
    }
    var btn = slotsEl.querySelector('.cart-slot-overlay__slot[data-slot-id="' + match.id + '"]');
    if (!btn) {
      state.prefill = null;
      return;
    }
    state.slot = match;
    state.equivalentTimeMatched = !!(
      state.context
      && state.previousReservation
      && match.id !== state.previousReservation.slotId
      && state.day === state.previousReservation.day
      && (match.description || '') === (state.previousReservation.timeShort || '')
    );
    Array.prototype.forEach.call(slotsEl.querySelectorAll('.cart-slot-overlay__slot'), function (el) {
      el.setAttribute('aria-pressed', String(el.getAttribute('data-slot-id') === match.id));
    });
    state.prefill = null;
    if (typeof btn.scrollIntoView === 'function') {
      btn.scrollIntoView({ block: 'nearest' });
    }
  }

  // ---------------------------------------------------------------------
  // Standalone mode (cart page / drawer closed)
  // ---------------------------------------------------------------------

  function isDrawerOpen() {
    return !!document.querySelector('.js-go-cart-drawer.is-open');
  }

  function ensureBackdrop() {
    var backdrop = document.getElementById('cart-slot-overlay-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = 'cart-slot-overlay-backdrop';
      backdrop.hidden = true;
      document.body.appendChild(backdrop);
      backdrop.addEventListener('click', function () { close(); });
    }
    return backdrop;
  }

  // .cart-slot-overlay normally lives inside .go-cart-drawer, whose parent
  // (.js-go-cart-drawer) is `transform`-ed off-screen while closed - any
  // `position: fixed` descendant of a transformed ancestor is positioned
  // relative to THAT ancestor, not the viewport, so a fixed panel would be
  // dragged off-screen with it. Detaching to <body> while standalone avoids
  // that trap; exitStandaloneMode() puts it straight back for drawer use.
  function enterStandaloneMode() {
    if (standaloneActive) return;
    standaloneActive = true;
    if (overlay.parentNode !== document.body) {
      standaloneHome = { parent: overlay.parentNode, nextSibling: overlay.nextSibling };
      document.body.appendChild(overlay);
    }
    overlay.classList.add('cart-slot-overlay--standalone');
    document.body.classList.add('cart-slot-overlay-open');
    ensureBackdrop().hidden = false;
  }

  function exitStandaloneMode() {
    if (!standaloneActive) {
      // Still make sure a stray backdrop isn't left visible (e.g. close()
      // called while never having entered standalone mode this session).
      var backdrop = document.getElementById('cart-slot-overlay-backdrop');
      if (backdrop) backdrop.hidden = true;
      return;
    }
    standaloneActive = false;
    overlay.classList.remove('cart-slot-overlay--standalone');
    document.body.classList.remove('cart-slot-overlay-open');
    var backdrop = document.getElementById('cart-slot-overlay-backdrop');
    if (backdrop) backdrop.hidden = true;
    if (standaloneHome) {
      if (standaloneHome.nextSibling) {
        standaloneHome.parent.insertBefore(overlay, standaloneHome.nextSibling);
      } else {
        standaloneHome.parent.appendChild(overlay);
      }
      standaloneHome = null;
    }
  }

  function setStandaloneMode() {
    if (isDrawerOpen()) {
      exitStandaloneMode();
    } else {
      enterStandaloneMode();
    }
  }

  // StorePicker experiment: use this exact slot UI inside the already-open
  // modal instead of presenting the drawer-shaped standalone panel. Moving
  // the one overlay keeps reservation behaviour and tracking surfaces shared.
  function enterStorePickerMode() {
    var host = document.querySelector('#store-picker-modal .store-picker__panel');
    if (!host) return false;
    if (storePickerActive && storePickerPanel === host) return true;

    exitStandaloneMode();
    storePickerActive = true;
    storePickerPanel = host;
    storePickerHome = { parent: overlay.parentNode, nextSibling: overlay.nextSibling };
    host.appendChild(overlay);
    storePickerA11y = {
      attributes: ['role', 'aria-modal', 'aria-labelledby', 'aria-describedby'].map(function (name) {
        var value = host.getAttribute(name);
        host.removeAttribute(name);
        return { name: name, value: value };
      }),
      children: Array.prototype.filter.call(host.children, function (child) { return child !== overlay; })
        .map(function (child) {
          var saved = { element: child, inert: child.inert, ariaHidden: child.getAttribute('aria-hidden') };
          child.inert = true;
          child.setAttribute('aria-hidden', 'true');
          return saved;
        })
    };
    host.classList.add('store-picker__panel--slots');
    overlay.classList.add('cart-slot-overlay--store-picker');
    if (backBtn) backBtn.setAttribute('aria-label', 'Back to store selection');
    return true;
  }

  function exitStorePickerMode() {
    if (!storePickerActive) return;
    storePickerActive = false;
    overlay.classList.remove('cart-slot-overlay--store-picker');
    if (backBtn) backBtn.setAttribute('aria-label', 'Back to cart');
    if (storePickerA11y) {
      storePickerA11y.children.forEach(function (saved) {
        saved.element.inert = saved.inert;
        if (saved.ariaHidden === null) saved.element.removeAttribute('aria-hidden');
        else saved.element.setAttribute('aria-hidden', saved.ariaHidden);
      });
      storePickerA11y.attributes.forEach(function (saved) {
        if (saved.value !== null) storePickerPanel.setAttribute(saved.name, saved.value);
      });
      storePickerA11y = null;
    }
    if (storePickerPanel) storePickerPanel.classList.remove('store-picker__panel--slots');
    storePickerPanel = null;

    if (storePickerHome) {
      if (storePickerHome.nextSibling) {
        storePickerHome.parent.insertBefore(overlay, storePickerHome.nextSibling);
      } else {
        storePickerHome.parent.appendChild(overlay);
      }
      storePickerHome = null;
    }
  }

  function setPresentationMode(options) {
    if (options && options.withinStorePicker && enterStorePickerMode()) return;
    exitStorePickerMode();
    setStandaloneMode();
  }

  /**
   * Compatibility entry point for callers that used to warm the cache.
   * Slot requests are now on demand: a closed selector must never fetch.
   */
  function prefetch() {
    if (!overlay || overlay.hidden || !readyToFetch()) return;
    var store = effectivePickingStore();
    var postcode = effectivePostcode();
    if (state.payload && state.fetchedFor
      && state.fetchedFor.store === store && state.fetchedFor.postcode === postcode) {
      return;
    }
    if (state.inFlight && state.inFlight.store === store && state.inFlight.postcode === postcode) {
      return;
    }
    fetchSlots(store, postcode, { quiet: true });
  }

  function finishClose(options, wasWithinStorePicker) {
    invalidateSlotRequest();
    state.reservationEpoch++;
    overlay.hidden = true;
    overlay.classList.remove('is-open');
    exitStandaloneMode();
    exitStorePickerMode();
    if (typeof removeTrapFocus === 'function') removeTrapFocus();

    if (wasWithinStorePicker && options.closeStorePicker
      && window.StorePicker && typeof window.StorePicker.close === 'function') {
      window.StorePicker.close();
    } else if (wasWithinStorePicker && options.restoreStorePickerFocus !== false
      && window.StorePicker && typeof window.StorePicker.restoreFocus === 'function') {
      window.StorePicker.restoreFocus(opener);
    } else if (!wasWithinStorePicker && opener && opener.isConnected) {
      opener.focus();
    }
    opener = null;

    state.context = null;
    state.commitContext = null;
    state.preserveCurrentReservation = false;
    state.previousReservation = null;
    state.equivalentTimeMatched = false;
    state.reservationPending = false;
    suspendedStorePickerModal = null;
    if (keepCurrentBtn) keepCurrentBtn.hidden = true;
  }

  function close(options) {
    if (!overlay) return true;
    options = options || {};
    if (state.reservationPending && !options.force) return false;
    finishClose(options, storePickerActive);
    return true;
  }

  // Once a replacement reservation succeeds, the product reconciliation
  // progress card becomes the only modal the shopper needs to see. Keep this
  // overlay's state mounted (finishReservationFlow still owns the final close)
  // but remove the slot panel visually so it cannot sit in front of the
  // product marquee. If reconciliation fails, restore the selector with the
  // existing error message and let the shopper retry.
  function suspendForCartReconciliation() {
    if (!overlay) return false;
    if (storePickerActive) {
      var storePickerModal = document.getElementById('store-picker-modal');
      if (storePickerModal && !storePickerModal.hidden) {
        storePickerModal.hidden = true;
        suspendedStorePickerModal = storePickerModal;
      }
    }
    overlay.hidden = true;
    overlay.classList.remove('is-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    return true;
  }

  function restoreAfterCartReconciliationFailure() {
    var pageOverlay = document.getElementById('pageOverlay');
    if (pageOverlay) pageOverlay.style.display = 'none';
    if (!overlay) return;
    if (suspendedStorePickerModal) {
      suspendedStorePickerModal.hidden = false;
      suspendedStorePickerModal = null;
    }
    overlay.hidden = false;
    overlay.classList.add('is-open');
    if (typeof trapFocus === 'function') trapFocus(overlay, closeBtn || overlay);
  }

  function readyToFetch() {
    if (state.context) return !!(state.context.postcode && state.context.store);
    return !!(currentPostcode() && currentPickingStore() && hasExplicitPickingStore());
  }

  function renderFromCardFallback() {
    if (!fromStoreEl) return;
    var store = effectivePickingStore();
    var name = (state.context && state.context.name) || storeDisplayName(store);
    fromStoreEl.textContent = formatFromLine(name, effectiveSuburb(), effectivePostcode());
  }

  function showMissingStore() {
    if (missingStoreState) missingStoreState.hidden = false;
    if (modesEl) modesEl.hidden = true;
    if (modeEx) modeEx.hidden = true;
    if (modeM) modeM.hidden = true;
    if (daysEl) { daysEl.hidden = true; daysEl.innerHTML = ''; }
    if (periodsEl) periodsEl.hidden = true;
    if (slotsEl) slotsEl.innerHTML = '';
    if (loadingEl) loadingEl.hidden = true;
    updateFooter();
  }

  function hideMissingStore() {
    if (missingStoreState) missingStoreState.hidden = true;
  }

  function showLoading(isLoading) {
    if (loadingEl) loadingEl.hidden = !isLoading;
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  function slotLoadErrorMessage() {
    var mode = state.lockedMode || state.mode;
    var timesLabel = mode === 'M'
      ? 'collection times'
      : (mode === 'EX' ? 'delivery times' : 'available times');
    return "We couldn't load " + timesLabel + ' right now. Please try again.';
  }

  // ---------------------------------------------------------------------
  // Fetch + cascade (delivery-slots)
  // ---------------------------------------------------------------------

  function fetchSlots(store, postcode, options) {
    var quiet = !!(options && options.quiet);
    // A slot refresh must not erase the explanation for a guarded reservation.
    if (!readReservationRecovery()) clearError();
    if (!quiet) {
      showLoading(true);
      if (daysEl) { daysEl.hidden = true; daysEl.innerHTML = ''; }
      if (periodsEl) periodsEl.hidden = true;
      if (slotsEl) slotsEl.innerHTML = '';
      if (modeEx) modeEx.hidden = true;
      if (modeM) modeM.hidden = true;
      if (modesEl) modesEl.hidden = true;
      updateFooter();
    }

    invalidateSlotRequest();
    if (!quiet) showLoading(true);
    var epoch = ++state.fetchEpoch;
    var params = new URLSearchParams({ picking_store: store, postcode: postcode });
    var request = requestJSON(storefrontApiURL + 'storefront/delivery-slots?' + params.toString());
    state.inFlight = { store: store, postcode: postcode, cancel: request.cancel };

    return request.then(function (data) {
      if (epoch !== state.fetchEpoch) return; // superseded by a newer open()/store change
      if (!data || !data.slots || typeof data.slots !== 'object') {
        throw new Error('invalid slots payload');
      }
      state.payload = data;
      state.fetchedFor = { store: store, postcode: postcode };
      // Only paint if the overlay is open (prefetch stays silent).
      if (overlay && !overlay.hidden) {
        renderAfterPayload();
      }
    }).catch(function (error) {
      if (epoch !== state.fetchEpoch) return;
      console.error('CartSlotOverlay: delivery-slots fetch failed:', error);
      if (!overlay || overlay.hidden) return;
      showError(slotLoadErrorMessage() + ' Close and reopen this panel to retry.');
    }).finally(function () {
      if (epoch === state.fetchEpoch) {
        state.inFlight = null;
        if (overlay && !overlay.hidden) showLoading(false);
      }
    });
  }

  function availableModes() {
    var modes = {};
    var slots = (state.payload && state.payload.slots) || {};
    Object.keys(slots).forEach(function (day) {
      Object.keys(slots[day]).forEach(function (mode) {
        var modeData = slots[day][mode];
        if (modeData && Array.isArray(modeData.time) && modeData.time.length) {
          modes[mode] = true;
        }
      });
    });
    return modes;
  }

  function renderAfterPayload() {
    var data = state.payload;
    if (!data) return;
    if (!readReservationRecovery()) clearError();
    showLoading(false);
    hideMissingStore();

    if (fromStoreEl) {
      var name = data.picking_store_name
        || (state.context && state.context.name)
        || storeDisplayName(effectivePickingStore());
      fromStoreEl.textContent = formatFromLine(name, effectiveSuburb(), data.postcode || effectivePostcode());
    }

    var modes = availableModes();
    if (!modes.EX && !modes.M) {
      if (modesEl) modesEl.hidden = true;
      if (modeEx) modeEx.hidden = true;
      if (modeM) modeM.hidden = true;
      if (daysEl) { daysEl.hidden = true; daysEl.innerHTML = ''; }
      if (periodsEl) periodsEl.hidden = true;
      renderSlotsEmpty(state.lockedMode === 'M'
        ? 'No Click & Collect times are available for this store right now.'
        : (state.lockedMode === 'EX'
          ? 'No delivery times are available for this postcode right now.'
          : 'No delivery or Click & Collect times are available for your store right now.'));
      updateFooter();
      return;
    }

    if (modesEl) modesEl.hidden = !!state.lockedMode;
    if (modeEx) modeEx.hidden = !modes.EX || !!state.lockedMode;
    if (modeM) modeM.hidden = !modes.M || !!state.lockedMode;

    if (state.lockedMode && !modes[state.lockedMode]) {
      state.mode = null;
      setModePressedState();
      if (daysEl) { daysEl.hidden = true; daysEl.innerHTML = ''; }
      if (periodsEl) periodsEl.hidden = true;
      renderSlotsEmpty(state.lockedMode === 'M'
        ? 'No Click & Collect times are available for this store right now.'
        : 'No delivery times are available for this postcode right now.');
      updateFooter();
      return;
    }

    // Only an existing reservation edit may preselect a mode. New journeys
    // require an explicit Delivery / Click & Collect choice.
    state.mode = state.lockedMode || ((state.prefill && state.prefill.mode && modes[state.prefill.mode])
      ? state.prefill.mode
      : null);
    setModePressedState();

    if (!state.mode) {
      if (daysEl) { daysEl.hidden = true; daysEl.innerHTML = ''; }
      if (periodsEl) periodsEl.hidden = true;
      renderSlotsEmpty('Choose Delivery or Click & Collect to see available days.');
      updateFooter();
      return;
    }

    renderDays();
  }

  // ---------------------------------------------------------------------
  // Mode pills
  // ---------------------------------------------------------------------

  function setModePressedState() {
    if (modeEx) modeEx.setAttribute('aria-pressed', String(state.mode === 'EX'));
    if (modeM) modeM.setAttribute('aria-pressed', String(state.mode === 'M'));
  }

  function bindModeEvents() {
    [modeEx, modeM].forEach(function (btn) {
      if (!btn) return;
      btn.addEventListener('click', function () {
        if (isReservationUnsettled()) return;
        if (state.lockedMode || btn.hidden || btn.disabled) return;
        var mode = btn.getAttribute('data-mode');
        if (state.mode === mode) return;
        state.mode = mode;
        writeStoredModeIntent(mode);
        state.day = null;
        state.period = null;
        state.slot = null;
        setModePressedState();
        renderDays();
      });
    });
  }

  // ---------------------------------------------------------------------
  // Days
  // ---------------------------------------------------------------------

  function sortedDayKeys() {
    return Object.keys((state.payload && state.payload.slots) || {}).sort();
  }

  function daysForMode(mode) {
    var slots = (state.payload && state.payload.slots) || {};
    return sortedDayKeys().filter(function (day) {
      var modeData = slots[day] && slots[day][mode];
      return !!(modeData && Array.isArray(modeData.time) && modeData.time.length);
    });
  }

  // dayStr is "YYYY-MM-DD" - parsed as local components (not via `new
  // Date(dayStr)`, which treats a bare YYYY-MM-DD as UTC and can land on the
  // wrong calendar day near midnight in AEST/AEDT).
  function parseDayKey(dayStr) {
    var parts = String(dayStr).split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  function formatDayLabel(dayStr) {
    var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var date = parseDayKey(dayStr);
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var diffDays = Math.round((date - today) / 86400000);
    // Manual parts — avoid locale strings (and Graphen has no digit glyphs if
    // a date span ever inherits the display face).
    var bottom = date.getDate() + ' ' + MONTHS[date.getMonth()];
    if (diffDays === 0) return { top: 'Today', bottom: bottom };
    if (diffDays === 1) return { top: 'Tomorrow', bottom: bottom };
    return { top: WEEKDAYS[date.getDay()], bottom: bottom };
  }

  function renderDays() {
    if (!daysEl) return;
    var days = daysForMode(state.mode);
    daysEl.innerHTML = '';

    if (!days.length) {
      daysEl.hidden = true;
      periodsEl.hidden = true;
      renderSlotsEmpty('No times available for this option right now.');
      updateFooter();
      return;
    }

    daysEl.hidden = false;
    if (!state.day || days.indexOf(state.day) === -1) {
      state.day = (state.prefill && state.prefill.day && days.indexOf(state.prefill.day) !== -1)
        ? state.prefill.day
        : null;
    }

    days.forEach(function (day) {
      var label = formatDayLabel(day);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cart-slot-overlay__day';
      btn.setAttribute('data-day', day);
      btn.setAttribute('aria-pressed', String(day === state.day));

      var top = document.createElement('span');
      top.className = 'cart-slot-overlay__day-label';
      top.textContent = label.top;

      var bottom = document.createElement('span');
      bottom.className = 'cart-slot-overlay__day-date';
      bottom.textContent = label.bottom;

      btn.appendChild(top);
      btn.appendChild(bottom);
      btn.addEventListener('click', function () {
        if (isReservationUnsettled()) return;
        if (state.day === day) return;
        state.day = day;
        state.period = null;
        state.slot = null;
        renderDays();
      });

      daysEl.appendChild(btn);
    });

    if (!state.day) {
      if (periodsEl) periodsEl.hidden = true;
      renderSlotsEmpty('Choose a day to see available times.');
      updateFooter();
      return;
    }

    renderPeriods();
  }

  // ---------------------------------------------------------------------
  // Morning / Afternoon / Evening periods
  // ---------------------------------------------------------------------

  // The slot id encodes its start time as "...-STARTHHMM-ENDHHMM" (e.g.
  // "20260731-76-EX-276-1130-1230" -> starts 11:30). Falls back to parsing
  // `description` (e.g. "11:30 AM-12:30 PM") if the id shape ever changes.
  function slotStartHour(slot) {
    var id = slot && slot.id;
    if (id) {
      var parts = String(id).split('-');
      var startSeg = parts[parts.length - 2];
      if (startSeg && /^\d{3,4}$/.test(startSeg)) {
        var padded = startSeg.length === 3 ? '0' + startSeg : startSeg;
        return parseInt(padded.slice(0, 2), 10);
      }
    }
    var desc = slot && slot.description;
    if (desc) {
      var match = String(desc).match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        var hour = parseInt(match[1], 10);
        var isPM = /PM/i.test(match[3]);
        if (isPM && hour !== 12) hour += 12;
        if (!isPM && hour === 12) hour = 0;
        return hour;
      }
    }
    return null;
  }

  function periodForHour(hour) {
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  }

  function currentModeTimes() {
    var slots = (state.payload && state.payload.slots) || {};
    var dayData = slots[state.day];
    var modeData = dayData && dayData[state.mode];
    return (modeData && Array.isArray(modeData.time)) ? modeData.time : [];
  }

  function periodCounts() {
    var counts = { morning: 0, afternoon: 0, evening: 0 };
    currentModeTimes().forEach(function (slot) {
      var hour = slotStartHour(slot);
      if (hour !== null) counts[periodForHour(hour)]++;
    });
    return counts;
  }

  function bindPeriodEvents() {
    if (!periodsEl) return;
    periodsEl.querySelectorAll('.cart-slot-overlay__period').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (isReservationUnsettled()) return;
        if (btn.hidden || btn.disabled) return;
        var key = btn.getAttribute('data-period');
        if (state.period === key) return;
        state.period = key;
        state.slot = null;
        renderPeriods();
      });
    });
  }

  function renderPeriods() {
    if (!periodsEl) return;
    var counts = periodCounts();
    var anyInfo = counts.morning + counts.afternoon + counts.evening > 0;
    var buttons = periodsEl.querySelectorAll('.cart-slot-overlay__period');

    // If none of the returned times parsed to a start hour, grouping is
    // meaningless - hide the pills and show everything ungrouped rather than
    // hiding every slot behind three empty pills.
    periodsEl.hidden = !anyInfo;

    buttons.forEach(function (btn) {
      var key = btn.getAttribute('data-period');
      var has = anyInfo && counts[key] > 0;
      btn.hidden = !anyInfo;
      btn.disabled = !has;
    });

    if (!anyInfo) {
      state.period = null;
    } else if (!state.period || !counts[state.period]) {
      // A prefill's period isn't stored directly - it's derived from the
      // reserved slot's start hour, once mode/day match the reservation.
      var prefillSlotForPeriod = findPrefillSlot();
      var prefillHour = prefillSlotForPeriod ? slotStartHour(prefillSlotForPeriod) : null;
      var prefillPeriod = prefillHour !== null ? periodForHour(prefillHour) : null;
      state.period = (prefillPeriod && counts[prefillPeriod])
        ? prefillPeriod
        : null;
    }

    buttons.forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(btn.getAttribute('data-period') === state.period));
    });

    if (anyInfo && !state.period) {
      renderSlotsEmpty('Choose a time of day to see available times.');
      updateFooter();
      return;
    }

    renderSlots();
  }

  // ---------------------------------------------------------------------
  // Slot list
  // ---------------------------------------------------------------------

  function renderSlotsEmpty(message) {
    if (!slotsEl) return;
    slotsEl.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'cart-slot-overlay__slots-empty';
    p.textContent = message;
    slotsEl.appendChild(p);
  }

  function renderSlots() {
    if (!slotsEl) return;
    slotsEl.innerHTML = '';

    var times = currentModeTimes();
    var filtered = state.period
      ? times.filter(function (slot) {
        var hour = slotStartHour(slot);
        return hour !== null && periodForHour(hour) === state.period;
      })
      : times;

    if (!filtered.length) {
      renderSlotsEmpty('No times available for this selection.');
      updateFooter();
      return;
    }

    filtered.forEach(function (slot) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cart-slot-overlay__slot';
      btn.setAttribute('data-slot-id', slot.id);
      btn.setAttribute('aria-pressed', String(!!state.slot && state.slot.id === slot.id));

      var label = document.createElement('span');
      label.textContent = slot.description || slot.value || slot.id;
      btn.appendChild(label);

      btn.addEventListener('click', function () {
        if (isReservationUnsettled()) return;
        state.slot = slot;
        state.equivalentTimeMatched = false;
        Array.prototype.forEach.call(slotsEl.querySelectorAll('.cart-slot-overlay__slot'), function (el) {
          el.setAttribute('aria-pressed', String(el.getAttribute('data-slot-id') === slot.id));
        });
        updateFooter();
        document.dispatchEvent(new CustomEvent('hfSlotSelected', {
          detail: { source: state.source, mode: state.mode, day: state.day, slot: slot },
          bubbles: true
        }));
      });

      slotsEl.appendChild(btn);
    });

    applyPrefillSelection();
    updateFooter();
  }

  function selectedSlotSummary() {
    if (!state.slot || !state.day) return '';
    var label = formatDayLabel(state.day);
    var dayText = (label.top === 'Today' || label.top === 'Tomorrow') ? label.top : (label.top + ' ' + label.bottom);
    var selectedTime = state.slot.description || state.slot.value || '';
    return dayText + ', ' + selectedTime;
  }

  function updateBasketNotice() {
    if (!basketNoticeEl) return;
    var candidate = state.context;
    var browsingStore = String(currentPickingStore() || '');
    var mode = state.lockedMode || state.mode || (candidate && candidate.shoppingIntent);
    var show = candidate && candidate.store && browsingStore && hasExplicitPickingStore()
      && String(candidate.store) !== browsingStore && (mode === 'EX' || mode === 'M')
      && !readReservationRecovery();
    basketNoticeEl.hidden = !show;
    if (!show) { basketNoticeEl.textContent = ''; return; }
    var name = candidate.name || storeDisplayName(candidate.store);
    basketNoticeEl.textContent = (mode === 'M'
      ? 'Your collection store is ' + name + '. '
      : 'Delivery for this postcode comes from ' + name + '. ')
      + 'Reserving a time will update your basket. Prices may change and unavailable items may be removed.';
  }

  function recoveryPrompt(recovery) {
    if (recovery.kind === 'release') return 'Your previous time still needs cancelling. Select '
      + (state.recoveryCheckFailed ? 'Retry cancellation' : 'Check cancellation')
      + ' below, or close this panel and try again shortly.';
    if (recovery.cartId !== getCartId() && recovery.releaseOfferedFor === getCartId()) {
      return 'Your cart changed. Select Release old time and choose again below, or close this panel to keep the earlier hold.';
    }
    var action = state.recoveryCheckFailed ? 'Retry check' : 'Check reservation';
    return 'Your previous time still needs confirmation. Select ' + action
      + ' below to finish before choosing another time.';
  }

  function updateFooter() {
    updateBasketNotice();
    if (!summaryEl || !reserveBtn) return;
    if (state.reservationPending) return;
    var recovery = readReservationRecovery();
    if (recovery) {
      // A reload resets state.slot while times load; retain the saved selection
      // in the footer rather than telling the shopper to select it again.
      var savedDay = formatDayLabel(recovery.day);
      var savedDayText = savedDay.top === 'Today' || savedDay.top === 'Tomorrow'
        ? savedDay.top : savedDay.top + ' ' + savedDay.bottom;
      summaryEl.textContent = savedDayText + ', '
        + (recovery.slot.description || recovery.slot.value || '');
      reserveBtn.disabled = false;
      reserveBtn.textContent = recovery.kind === 'release'
        ? (state.recoveryCheckFailed ? 'Retry cancellation' : 'Check cancellation')
        : recovery.cartId !== getCartId() && recovery.releaseOfferedFor === getCartId()
        ? 'Release old time and choose again'
        : (state.recoveryCheckFailed ? 'Retry check' : 'Check reservation');
      if (keepCurrentBtn) keepCurrentBtn.hidden = true;
      if (errorEl && errorEl.hidden) showError(recoveryPrompt(recovery));
      return;
    }
    if (keepCurrentBtn) {
      keepCurrentBtn.hidden = !state.preserveCurrentReservation;
      if (state.preserveCurrentReservation && state.previousReservation) {
        keepCurrentBtn.textContent = state.previousReservation.mode === 'M'
          ? 'Keep current Click & Collect'
          : 'Keep current delivery';
      }
    }
    if (state.slot && state.day) {
      var selectedSummary = selectedSlotSummary();
      summaryEl.textContent = state.equivalentTimeMatched
        ? ('Same time available for ' + modeLabel(state.mode) + ': ' + selectedSummary)
        : selectedSummary;
      reserveBtn.textContent = state.equivalentTimeMatched
        ? (state.mode === 'M' ? 'Use this collection time' : 'Use this delivery time')
        : 'Reserve time';
      reserveBtn.disabled = false;
    } else {
      summaryEl.textContent = 'Select a day and time';
      reserveBtn.textContent = 'Reserve time';
      reserveBtn.disabled = true;
    }
  }

  // ---------------------------------------------------------------------
  // Reserve (v1, for staging preview)
  // ---------------------------------------------------------------------

  // The slot id ("unique_window") is store-specific:
  // "YYYYMMDD-STORE-TYPE-ZONE-START-END" - mirrors
  // delivery-drawer-functions.js's getStoreFromUniqueWindow().
  function storeFromUniqueWindow(uniqueWindow) {
    var parts = String(uniqueWindow || '').split('-');
    return (parts.length >= 2 && /^\d+$/.test(parts[1])) ? parts[1] : null;
  }

  function modeFromUniqueWindow(uniqueWindow) {
    var parts = String(uniqueWindow || '').split('-');
    return parts.length >= 3 ? parts[2] : null;
  }

  // The legacy reservations.php call (delivery-drawer-functions.js) always
  // sends `cart_id: getCookie('cart')` - the Shopify cart token cookie, not
  // the session cookie - and storefront/reservations is documented as its
  // direct successor (same params). Prefer that proven value; only fall
  // back to the session-ish identifiers if "cart" isn't set yet (e.g. the
  // very first pageview before Shopify issues it).
  function getCartId() {
    var cartCookie = typeof getCookie === 'function' && getCookie('cart');
    if (cartCookie) return cartCookie;
    var sid = (window.siteWideSettings && window.siteWideSettings.shopifySID)
      || (typeof getCookie === 'function' && getCookie('_shopify_s'));
    return sid || '';
  }

  function isLocationExemptCartItem(item) {
    var title = String(item && (item.title || item.product_title) || '').toLowerCase();
    var handle = String(item && item.handle || '').toLowerCase();
    return (item && item.requires_shipping === false)
      || Boolean(item && item.gift_card)
      || title.indexOf('delivery fee') !== -1
      || title.indexOf('service fee') !== -1
      || title.indexOf('gift card') !== -1
      || title.indexOf('delivery pass') !== -1
      || title.indexOf('pay-it-forward') !== -1
      || title.indexOf('pay it forward') !== -1
      || handle === 'delivery'
      || handle === 'service-fee'
      || handle.indexOf('pay-it-forward') !== -1;
  }

  function verifyCartAtStore(store) {
    return requestJSON('/cart.js', { credentials: 'same-origin' }).then(function (cart) {
      var attributes = cart && cart.attributes ? cart.attributes : {};
      var staleAttribute = String(attributes.picking_store || '') !== String(store);
      var staleLine = (cart.items || []).some(function (item) {
        if (isLocationExemptCartItem(item)) return false;
        var properties = item && item.properties ? item.properties : {};
        return String(properties._location_id || '') !== String(store)
          || !(parseFloat(properties._location_price) > 0);
      });
      if (staleAttribute || staleLine) {
        throw new Error('cart did not finish reconciling to store ' + store);
      }
      return cart;
    });
  }

  // A timeout can outlive a successful Shopify write. Check the saved order
  // snapshot before asking a large cart to repeat its store reconciliation.
  function readCompletedReservationCart(context) {
    return requestJSON('/cart.js', { credentials: 'same-origin' }).then(function (cart) {
      var attrs = cart && cart.attributes || {};
      if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
      if (String(attrs.picking_store || '') !== String(context.store)
        || String(attrs.unique_window || '') !== String(context.slot.id)
        || String(attrs.delivery_date || '') !== String(context.day)
        || String(attrs.delivery_type || '') !== String(context.mode)
        || String(attrs.postcode || '') !== String(context.postcode)
        || !String(attrs.courier || '').trim()
        || !String(attrs.delivery_run || '').trim()
        || !String(attrs.local_delivery_request || '').trim()
        || (context.mode === 'M' && String(attrs.delivery_cc_store || '') !== String(context.store))
        || (context.mode === 'EX' && (!String(attrs.delivery_suburb || '').trim()
          || (context.candidate && context.candidate.suburb
            && String(attrs.delivery_suburb) !== String(context.candidate.suburb))))
        || !(Number(attrs.reconcile_v || 0) >= 3)) return null;
      var linesMatch = cart.items.every(function (item) {
        if (isLocationExemptCartItem(item)) return true;
        var properties = item.properties || {};
        return String(properties._location_id || '') === String(context.store)
          && parseFloat(properties._location_price) > 0;
      });
      return linesMatch ? cart : null;
    });
  }

  function dispatchCommittedContext(context) {
    if (!context) return;
    document.dispatchEvent(new CustomEvent('locationChanged', {
      detail: {
        newLocation: context.store,
        locationName: context.name || storeDisplayName(context.store),
        deferNavigation: true,
        shoppingIntent: context.shoppingIntent,
        reservationReplaced: true
      },
      bubbles: true
    }));
    document.dispatchEvent(new CustomEvent('hfPickingStoreChanged', {
      detail: {
        store: context.store,
        suburb: context.suburb,
        postcode: context.postcode,
        shoppingIntent: context.shoppingIntent,
        reservationReplaced: true
      },
      bubbles: true
    }));
  }

  function sameReservationAttempt(recovery) {
    var latest = readReservationRecovery();
    return latest && latest.cartId === recovery.cartId && latest.startedAt === recovery.startedAt ? latest : null;
  }

  function reservationMatches(data, recovery) {
    var response = data && data.response;
    return !!(data && data.status === 'SUCCESS' && response
      && String(response.cart_id || '') === recovery.cartId
      && String(response.unique_window || '') === recovery.slot.id
      && Number(response.seconds_remaining) > 0);
  }

  function reservationLookup(recovery, guard) {
    var params = new URLSearchParams({ action: 'get', cart_id: recovery.cartId });
    return requestJSON(storefrontApiURL + 'storefront/reservations?' + params.toString())
      .then(function (data) {
        if (guard) guard();
        var response = data && data.response;
        if (reservationMatches(data, recovery)) {
          if (recovery.releasePendingFor) throw new Error('The earlier reservation release is still pending');
          recovery.settled = true;
          if (sameReservationAttempt(recovery)) saveReservationRecovery(recovery);
          return data;
        }
        var absent = data && data.status === 'FAILED'
          && response === 'Reservation does not exist or has expired';
        // Only a completed original write plus a terminal read can prove that
        // no late write is still capable of replacing the cart's reservation.
        // Absence immediately after a timeout alone does not prove failure.
        var latest = sameReservationAttempt(recovery);
        if (latest && latest.rejected && data && data.status === 'SUCCESS'
          && response && String(response.cart_id || '') === recovery.cartId
          && String(response.unique_window || '') === recovery.previousSlotId
          && Number(response.seconds_remaining) > 0) {
          saveReservationRecovery(null);
          var rejected = new Error('The selected reservation was rejected; the previous hold is confirmed');
          rejected.reservationRejected = true;
          throw rejected;
        }
        if (absent && latest && (recovery.settled || latest.settled)) {
          // The browser may have returned to the cart targeted by an uncertain
          // DELETE. Absence cannot prove that delayed DELETE will not erase a
          // later SET on this same cart, so keep its reservation guard intact.
          if (recovery.releasePendingFor) throw new Error('The earlier reservation release is still pending');
          saveReservationRecovery(null);
          if (typeof removeCookie === 'function') {
            removeCookie('delivery_expires_time');
            removeCookie('delivery_unique_window');
          }
          var expired = new Error('Reservation has expired');
          expired.reservationExpired = true;
          throw expired;
        }
        throw new Error('The reservation outcome is not confirmed');
      });
  }

  function reservationIsAbsent(data) {
    return data && data.status === 'FAILED'
      && data.response === 'Reservation does not exist or has expired';
  }

  function assertRecoveryCart(recovery, cartId, guard) {
    if (guard) guard();
    if (getCartId() !== cartId || !sameReservationAttempt(recovery)) {
      throw new Error('Cart identity changed during reservation recovery');
    }
  }

  function readReservationForCart(cartId) {
    var params = new URLSearchParams({ action: 'get', cart_id: cartId });
    return requestJSON(storefrontApiURL + 'storefront/reservations?' + params.toString());
  }

  function clearChangedCartReservation(recovery, cartId, guard) {
    assertRecoveryCart(recovery, cartId, guard);
    var cookieWindow = typeof getCookie === 'function' && getCookie('delivery_unique_window');
    if (cookieWindow && cookieWindow !== recovery.slot.id) {
      throw new Error('Reservation details changed while checking the cart');
    }
    // Both cart reads proved there is no current hold. Remove only reservation
    // routing, preserving the shopper's browsing store/postcode/suburb.
    if (typeof removeCookie !== 'function' || typeof syncDeliveryAttributesToCart !== 'function') {
      throw new Error('Reservation details cannot be safely cleared');
    }
    // A cancelled hold must not trigger the legacy expiry poll on its next tick.
    if (typeof resEndIntHandle !== 'undefined' && resEndIntHandle) {
      clearInterval(resEndIntHandle);
      resEndIntHandle = null;
    }
    ['delivery_expires_time', 'delivery_unique_window', 'delivery_type', 'delivery_date',
      'delivery_time', 'delivery_time_short', 'delivery_courier', 'delivery_run', 'delivery_cc_store']
      .forEach(function (name) { removeCookie(name); });
    try {
      localStorage.removeItem('activeDelivery');
      localStorage.removeItem('clickCollectSelected');
    } catch (e) { /* cookies and the recovery guard remain authoritative */ }
    document.body.classList.remove('click-collect-active');
    state.slot = null;
    state.prefill = null;
    if (slotsEl) Array.prototype.forEach.call(slotsEl.querySelectorAll('.cart-slot-overlay__slot'), function (button) {
      button.setAttribute('aria-pressed', 'false');
    });
    return Promise.resolve(syncDeliveryAttributesToCart()).then(function () {
      assertRecoveryCart(recovery, cartId, guard);
      return requestJSON('/cart.js', { credentials: 'same-origin' }).catch(function (error) {
        error.reservationRoutingUnconfirmed = true;
        throw error;
      });
    }).then(function (cart) {
      assertRecoveryCart(recovery, cartId, guard);
      // A completed sync is not proof that Shopify accepted the cleanup. These
      // are exactly the slot-owned keys from getDeliveryAttributesFromCookies;
      // picking_store and unrelated cart attributes remain untouched.
      var attributes = cart && cart.attributes;
      var reservationKeys = ['delivery_date', 'delivery_run', 'courier', 'unique_window',
        'delivery_type', 'delivery_cc_store', 'postcode', 'delivery_suburb', 'local_delivery_request'];
      if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)
        || reservationKeys.some(function (key) {
          return attributes[key] !== undefined && attributes[key] !== null && attributes[key] !== '';
        })) {
        var unconfirmed = new Error('Cart reservation routing cleanup is not confirmed');
        unconfirmed.reservationRoutingUnconfirmed = true;
        throw unconfirmed;
      }
      saveReservationRecovery(null);
      window.__hfDeliveryReservationUpdating = false;
      if (typeof window.cartDeliveryDetails === 'function') window.cartDeliveryDetails();
      if (window.agileCart && typeof window.agileCart.fetchCart === 'function') window.agileCart.fetchCart();
      var reselect = new Error('The earlier hold has ended; select a time for the current cart');
      reselect.reservationReselect = true;
      throw reselect;
    });
  }

  function recoverChangedCart(recovery, cartId, releaseRequested, guard) {
    assertRecoveryCart(recovery, cartId, guard);
    // A previously displayed release option is not fresh proof. Re-read both
    // exact identities before an explicitly requested delete can go on the wire.
    delete recovery.releaseOfferedFor;
    saveReservationRecovery(recovery);
    return readReservationForCart(cartId).then(function (current) {
      assertRecoveryCart(recovery, cartId, guard);
      if (reservationMatches(current, { cartId: cartId, slot: recovery.slot })) {
        // This is the current cart's own confirmed hold, never a transplant of
        // the original cart's response or a guess that two token strings match.
        recovery.cartId = cartId;
        recovery.settled = true;
        delete recovery.releasePendingFor;
        saveReservationRecovery(recovery);
        return current;
      }
      if (!reservationIsAbsent(current)) throw new Error('The current cart reservation is not confirmed');
      return readReservationForCart(recovery.cartId).then(function (original) {
        assertRecoveryCart(recovery, cartId, guard);
        var latest = sameReservationAttempt(recovery);
        if (reservationIsAbsent(original) && (recovery.settled || latest.settled)) {
          return readReservationForCart(cartId).then(function (confirmedCurrent) {
            assertRecoveryCart(recovery, cartId, guard);
            if (!reservationIsAbsent(confirmedCurrent)) throw new Error('The current cart reservation changed during recovery');
            return clearChangedCartReservation(recovery, cartId, guard);
          });
        }
        if (!reservationMatches(original, recovery)) throw new Error('The original reservation is not confirmed');
        recovery.settled = true;
        if (!releaseRequested || recovery.releasePendingFor) {
          // An uncertain delete is checked, never replayed automatically.
          if (!recovery.releasePendingFor) recovery.releaseOfferedFor = cartId;
          saveReservationRecovery(recovery);
          var pending = new Error('The earlier cart still holds the selected time');
          pending.reservationReleaseRequired = !recovery.releasePendingFor;
          throw pending;
        }
        recovery.releasePendingFor = cartId;
        saveReservationRecovery(recovery);
        assertRecoveryCart(recovery, cartId, guard);
        var params = new URLSearchParams({ action: 'delete', cart_id: recovery.cartId });
        return requestJSON(storefrontApiURL + 'storefront/reservations?' + params.toString())
          .catch(function (error) {
            console.warn('CartSlotOverlay: release response unconfirmed; checking the original cart:', error);
          }).then(function () {
            assertRecoveryCart(recovery, cartId, guard);
            // A delete response alone is not proof. Absence must be read back
            // before old routing is cleared or a new Reserve becomes possible.
            return readReservationForCart(recovery.cartId);
          }).then(function (released) {
            assertRecoveryCart(recovery, cartId, guard);
            if (!reservationIsAbsent(released)) throw new Error('The earlier reservation has not been confirmed released');
            // Recheck current absence after release; another tab may have
            // reserved this cart while the original-cart request was running.
            return readReservationForCart(cartId);
          }).then(function (currentAfterRelease) {
            assertRecoveryCart(recovery, cartId, guard);
            if (!reservationIsAbsent(currentAfterRelease)) throw new Error('The current cart reservation changed during release');
            return clearChangedCartReservation(recovery, cartId, guard);
          });
      });
    });
  }

  // Browsing cancels a hold before its route can be cleared. Keep an uncertain
  // cart-keyed DELETE durable: an absent read alone cannot cancel a late write.
  var browseReleasePromise = null;
  var browseCleanupPromise = null;

  function releaseForBrowse() {
    if (browseReleasePromise) return browseReleasePromise;
    var recovery = readReservationRecovery();
    var cartId = getCartId();
    if (state.reservationPending || (recovery && recovery.kind !== 'release')) {
      return Promise.reject(new Error('Please finish checking your previous reservation before browsing another store.'));
    }
    if (!recovery) {
      var windowId = typeof getCookie === 'function' && getCookie('delivery_unique_window');
      if (!windowId) return Promise.resolve(true);
      if (!cartId) return Promise.reject(new Error('Your cart could not be identified. Please refresh and try again.'));
      recovery = {
        kind: 'release', cartId: cartId, startedAt: Date.now(), settled: true,
        slot: { id: windowId, description: getCookie('delivery_time_short') || '', value: getCookie('delivery_time') || '' },
        day: getCookie('delivery_date') || '', mode: getCookie('delivery_type') || 'EX',
        context: { store: currentPickingStore(), postcode: currentPostcode(), suburb: currentSuburb(), name: storeDisplayName(currentPickingStore()) },
        deleteStarted: false, deleteSettled: false
      };
      saveReservationRecovery(recovery);
    }
    var finished = false;
    var timer;
    function guard() {
      if (finished || getCartId() !== cartId || recovery.cartId !== cartId || !sameReservationAttempt(recovery)) {
        throw new Error('Your cart changed while cancelling. Please check your reservation again.');
      }
    }
    function recordDeleteResult(data) {
      if (!data || (data.status !== 'SUCCESS' && data.status !== 'FAILED')) return false;
      var latest = sameReservationAttempt(recovery);
      if (latest && latest.kind === 'release') {
        latest.deleteSettled = true;
        latest.deleteSucceeded = data.status === 'SUCCESS';
        saveReservationRecovery(latest);
        recovery = latest;
      }
      return true;
    }
    function cleanup() {
      guard();
      if (browseCleanupPromise) throw new Error('Your cart is still updating. Please retry shortly.');
      var cleanupWork = clearChangedCartReservation(recovery, cartId, guard);
      browseCleanupPromise = cleanupWork;
      return cleanupWork.catch(function (error) {
        if (!error.reservationReselect) throw error;
        return true;
      }).finally(function () {
        if (browseCleanupPromise === cleanupWork) browseCleanupPromise = null;
      });
    }
    function verifyAfterDelete() {
      guard();
      return readReservationForCart(cartId).then(function (data) {
        guard();
        var latest = sameReservationAttempt(recovery);
        if (!latest || !latest.deleteSettled) throw new Error('Cancellation has not been confirmed yet.');
        if (!reservationIsAbsent(data)) throw new Error('Your time has not been cancelled. Please retry.');
        return cleanup();
      });
    }
    var work = Promise.resolve().then(function () {
      guard();
      lockCheckout();
      return readReservationForCart(cartId);
    }).then(function (data) {
      guard();
      recovery = sameReservationAttempt(recovery);
      if (recovery.deleteStarted && !recovery.deleteSettled) {
        // Retry checks the existing operation; never overlaps another DELETE.
        throw new Error('Cancellation has not been confirmed yet.');
      }
      if (reservationIsAbsent(data)) return cleanup();
      if (!reservationMatches(data, recovery)) throw new Error('Your reservation changed. Please check your time again.');
      recovery.deleteStarted = true;
      recovery.deleteSettled = false;
      saveReservationRecovery(recovery);
      var params = new URLSearchParams({ action: 'delete', cart_id: cartId });
      return requestJSON(storefrontApiURL + 'storefront/reservations?' + params.toString(), {}, recordDeleteResult)
        .then(function (result) {
          if (!recordDeleteResult(result)) throw new Error('The cancellation response could not be confirmed.');
          return verifyAfterDelete();
        });
    });
    var bounded = Promise.race([work, new Promise(function (resolve, reject) {
      timer = setTimeout(function () { reject(new Error('Cancellation or cart update timed out.')); }, CART_UPDATE_TIMEOUT_MS);
    })]);
    browseReleasePromise = bounded.catch(function (error) {
      console.warn('CartSlotOverlay: browse reservation cancellation did not complete:', error);
      error.userMessage = "We couldn't confirm cancellation. Your browsing store has not changed. Retry cancelling, or close and try again later.";
      error.reservationReleaseUncertain = true;
      throw error;
    }).finally(function () {
      finished = true;
      clearTimeout(timer);
      browseReleasePromise = null;
    });
    return browseReleasePromise;
  }

  function checkBrowseReleaseFromSelector() {
    // releaseForBrowse owns its watchdog/marker and never starts a new booking.
    var request = releaseForBrowse();
    state.reservationPending = true;
    reserveBtn.disabled = true;
    reserveBtn.textContent = 'Checking cancellation…';
    return request.then(function () {
      state.slot = null;
      showError('Your previous time has been cancelled. Choose a time again when you are ready.');
    }).catch(function (error) {
      state.recoveryCheckFailed = true;
      showError("We couldn't confirm cancellation. Select Retry cancellation below, or close this panel and try again shortly.");
    }).finally(function () {
      state.reservationPending = false;
      updateFooter();
    });
  }

  function reserveSelectedSlot(prepared) {
    if (!reserveBtn || reserveBtn.disabled || state.reservationPending) return;
    var recovery = readReservationRecovery();
    if (recovery && recovery.kind === 'release') return checkBrowseReleaseFromSelector();
    if (!recovery && !state.slot) return;

    var currentStore = String(effectivePickingStore() || '');
    var currentPostcodeValue = String(effectivePostcode() || '');
    if (!recovery) {
      var slotStore = storeFromUniqueWindow(state.slot.id);
      var slotMode = modeFromUniqueWindow(state.slot.id);
      var fetchedForCurrentContext = state.fetchedFor
        && String(state.fetchedFor.store || '') === currentStore
        && String(state.fetchedFor.postcode || '') === currentPostcodeValue;
      if (slotStore !== currentStore || slotMode !== state.mode || !fetchedForCurrentContext) {
        console.warn('CartSlotOverlay: blocked stale slot at reserve');
        state.slot = null;
        updateFooter();
        showError('Your shopping location changed. Please choose a time again.');
        if (currentStore && currentPostcodeValue) fetchSlots(currentStore, currentPostcodeValue, { quiet: false });
        return;
      }
    }

    // Materialize Shopify's cart before reserving. An empty/new session can
    // otherwise reserve against the SID and acquire a different cart identity
    // during the later attribute sync, stranding a successful hold.
    if (!prepared && !recovery) {
      var preparationEpoch = ++state.reservationEpoch;
      var preparingSlot = state.slot.id;
      state.reservationPending = true;
      reserveBtn.disabled = true;
      reserveBtn.textContent = 'Reserving…';
      clearError();
      return requestJSON('/cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: {} })
      }).then(function (cart) {
        if (preparationEpoch !== state.reservationEpoch) return;
        if (!cart || !Array.isArray(cart.items) || !cart.token
          || !(typeof getCookie === 'function' && getCookie('cart'))) {
          throw new Error('Shopify cart identity is not ready');
        }
        if (!state.slot || state.slot.id !== preparingSlot) throw new Error('Selected time changed');
        state.reservationPending = false;
        reserveBtn.disabled = false;
        return reserveSelectedSlot(true);
      }).catch(function (error) {
        if (preparationEpoch !== state.reservationEpoch) return;
        showError("We couldn't reserve that time. Please try again.");
      }).finally(function () {
        if (preparationEpoch !== state.reservationEpoch) return;
        state.reservationPending = false;
        updateFooter();
      });
    }

    var cartId = getCartId();
    if (!cartId) {
      showError("We couldn't identify your cart. Please refresh and try again.");
      return;
    }
    var checkingRecovery = !!recovery;
    var releaseRequested = !!(recovery && recovery.cartId !== cartId && recovery.releaseOfferedFor === cartId);
    state.recoveryCheckFailed = false;
    var reservationContext = {
      store: recovery ? recovery.context.store : currentStore,
      postcode: recovery ? recovery.context.postcode : currentPostcodeValue,
      mode: recovery ? recovery.mode : state.mode,
      day: recovery ? recovery.day : state.day,
      slot: recovery ? recovery.slot : Object.assign({}, state.slot),
      candidate: recovery ? recovery.context : (state.context ? Object.assign({}, state.context) : null),
      commitContext: recovery ? null : state.commitContext
    };
    if (!recovery) {
      recovery = {
        cartId: cartId, startedAt: Date.now(), settled: false,
        previousSlotId: (typeof getCookie === 'function' && getCookie('delivery_unique_window')) || '',
        slot: reservationContext.slot, day: reservationContext.day, mode: reservationContext.mode,
        context: {
          store: currentStore, postcode: currentPostcodeValue,
          suburb: effectiveSuburb(), name: storeDisplayName(currentStore),
          shoppingIntent: reservationContext.mode
        }
      };
      // Persist before action=set so navigation/reload cannot forget an
      // unresolved mutation and incorrectly trust the previous reservation.
      saveReservationRecovery(recovery);
    }

    var epoch = ++state.reservationEpoch;
    var reservationSucceeded = false;
    var slotPickerSuspended = false;
    var finished = false;
    var cartWatchdog;
    state.reservationPending = true;
    state.reservationOperation = epoch;
    reserveBtn.disabled = true;
    reserveBtn.textContent = checkingRecovery ? 'Checking reservation…' : 'Reserving…';
    if (keepCurrentBtn) keepCurrentBtn.disabled = true;
    // Keep the warning and selected times visible during a read-only recheck.
    if (!checkingRecovery) clearError();
    lockCheckout();

    function assertJourney() {
      if (finished || epoch !== state.reservationEpoch || getCartId() !== cartId) {
        throw new Error('Reservation journey changed before completion');
      }
    }

    function assertCurrent() {
      assertJourney();
      if (recovery.cartId !== cartId) throw new Error('Reservation belongs to a different cart');
      if (!checkingRecovery && (
        String(effectivePickingStore() || '') !== reservationContext.store
        || String(effectivePostcode() || '') !== reservationContext.postcode
        || state.mode !== reservationContext.mode
        || !state.slot || state.slot.id !== reservationContext.slot.id)) {
        throw new Error('Shopping location changed before completion');
      }
    }

    function lookup() {
      if (epoch === state.reservationEpoch && reserveBtn) reserveBtn.textContent = 'Checking reservation…';
      return Promise.resolve().then(function () {
        return recovery.cartId === cartId
          ? reservationLookup(recovery, assertJourney)
          : recoverChangedCart(recovery, cartId, releaseRequested, assertJourney);
      });
    }

    var request;
    if (checkingRecovery) {
      request = lookup();
    } else {
      var params = new URLSearchParams({ action: 'set', cart_id: cartId, unique_window: recovery.slot.id });
      request = requestJSON(storefrontApiURL + 'storefront/reservations?' + params.toString(), {}, function (data) {
        // A fetch implementation may finish even after cancellation. Record
        // proof only; a late callback never applies cookies or closes a panel.
        var latest = sameReservationAttempt(recovery);
        if (latest && data && (reservationMatches(data, recovery) || data.status === 'FAILED')) {
          latest.settled = true;
          latest.rejected = data.status === 'FAILED';
          saveReservationRecovery(latest);
        }
      }).then(function (data) {
        if (data && data.status === 'FAILED') {
          if (sameReservationAttempt(recovery)) {
            if (getCartId() === recovery.cartId) saveReservationRecovery(null);
            else {
              recovery.settled = true;
              recovery.rejected = true;
              saveReservationRecovery(recovery);
            }
          }
          var rejected = new Error('The selected reservation was rejected');
          rejected.reservationRejected = true;
          throw rejected;
        }
        if (!reservationMatches(data, recovery)) throw new Error('Invalid reservation response');
        recovery.settled = true;
        if (sameReservationAttempt(recovery)) saveReservationRecovery(recovery);
        return data;
      }).catch(function (error) {
        if (error.reservationRejected) throw error;
        console.warn('CartSlotOverlay: reserve response unconfirmed; checking the same cart:', error);
        return lookup();
      });
    }

    var completion = request.then(function (data) {
      reservationSucceeded = true;
      assertCurrent();
      var cartCheck = checkingRecovery
        ? readCompletedReservationCart(reservationContext)
        : Promise.resolve(false);
      return cartCheck.then(function (cartAlreadyComplete) {
        assertCurrent();
        if (checkingRecovery && pendingCartUpdate) {
          throw new Error('The existing cart update is still running');
        }
        // All completion writes use the immutable attempted selection. UI edits
        // and late responses can never substitute a different unique_window.
        state.mode = reservationContext.mode;
        state.day = reservationContext.day;
        state.slot = reservationContext.slot;
        var commitResult = reservationContext.commitContext ? reservationContext.commitContext() : true;
        return Promise.resolve(commitResult).then(function (committed) {
          assertCurrent();
          if (committed === false) throw new Error('Candidate shopping location could not be committed');
          if (reservationContext.candidate && typeof setCookie === 'function') {
            setCookie('postcode', reservationContext.candidate.postcode, 365);
            setCookie('suburb', reservationContext.candidate.suburb || '', 365);
          }
          window.__hfDeliveryReservationUpdating = true;
          var cookieData = cartAlreadyComplete ? Object.assign({}, data, {
            response: Object.assign({}, data.response, {
              courier: cartAlreadyComplete.attributes.courier,
              delivery_run: cartAlreadyComplete.attributes.delivery_run
            })
          }) : data;
          var cookieInfo = applyReservationCookies(cookieData);
          if (!cookieInfo) throw new Error('Reservation details could not be saved');
          var eventSource = reservationContext.candidate ? 'cart-page-replacement' : state.source;
          function finish() {
            assertCurrent();
            document.dispatchEvent(new CustomEvent('hfSlotReserved', {
              detail: { source: eventSource, mode: reservationContext.mode, day: reservationContext.day,
                slot: reservationContext.slot, response: data }, bubbles: true
            }));
            saveReservationRecovery(null);
            state.reservationPending = false;
            if (reserveBtn) reserveBtn.textContent = 'Reserved!';
            finishReservationFlow();
          }
          if (cartAlreadyComplete) {
            // Normal cart/fee checkout guards still run after this modal closes.
            // No second reservation, cart write or full reconciliation is needed.
            finish();
            dispatchCommittedContext(reservationContext.candidate);
            return;
          }
          if (!reservationContext.candidate) {
            finish();
            backgroundEnrichAndSync(data, cookieInfo);
            return;
          }
          reserveBtn.textContent = 'Updating cart…';
          var syncPromise = typeof syncDeliveryAttributesToCart === 'function'
            ? syncDeliveryAttributesToCart()
            : Promise.reject(new Error('Cart attribute sync is unavailable'));
          return syncPromise.then(function () {
            assertCurrent();
            if (!window.agileCart || typeof window.agileCart.updateCartItems !== 'function') {
              throw new Error('Cart reconciliation is unavailable');
            }
            slotPickerSuspended = suspendForCartReconciliation();
            var update = Promise.resolve(window.agileCart.updateCartItems({ progressContext: {
              mode: reservationContext.mode, timeLabel: selectedSlotSummary(),
              storeName: reservationContext.candidate.name || storeDisplayName(reservationContext.store)
            } }));
            pendingCartUpdate = update;
            return update.finally(function () {
              if (pendingCartUpdate === update) pendingCartUpdate = null;
            });
          }).then(function () {
            assertCurrent();
            return verifyCartAtStore(reservationContext.store);
          }).then(function () {
            assertCurrent();
            return backgroundEnrichAndSync(data, cookieInfo, { requireSync: true });
          }).then(function () {
            finish();
            dispatchCommittedContext(reservationContext.candidate);
          });
        });
      });
    });

    // Cart reconciliation has its own queue/watchdogs. Bound this UI wait too;
    // a stalled downstream stage must reopen Close without unlocking checkout.
    var boundedCompletion = Promise.race([completion, new Promise(function (resolve, reject) {
      cartWatchdog = setTimeout(function () { reject(new Error('Reservation/cart update timed out')); }, CART_UPDATE_TIMEOUT_MS);
    })]);
    return boundedCompletion.catch(function (error) {
      console.warn('CartSlotOverlay: reservation flow did not complete:', error);
      if (epoch === state.reservationEpoch && slotPickerSuspended) restoreAfterCartReconciliationFailure();
      if (state.reservationOperation === epoch) {
        state.reservationPending = false;
        if (keepCurrentBtn) keepCurrentBtn.disabled = false;
      }
      if (epoch !== state.reservationEpoch || !overlay || overlay.hidden) return;
      state.recoveryCheckFailed = checkingRecovery && !!readReservationRecovery();
      var ordinaryFailure = error.reservationRejected
        ? 'That time could not be reserved. Please choose another time.'
        : (error.reservationExpired
          ? 'That reservation has expired. Please choose a time again.'
          : (checkingRecovery
            ? (reservationSucceeded
              ? "Your time is reserved, but we couldn't finish checking your cart. Select Retry check below, or close this panel and try again shortly."
              : "We couldn't confirm your time. Select Retry check below, or close this panel and try again shortly. Other times will be available once your previous booking is resolved.")
            : (reservationSucceeded
              ? 'Your time is reserved, but your cart still needs updating. Select Check reservation below to finish, or close this panel and try again shortly.'
              : "We couldn't confirm your time. Select Check reservation below before choosing another time, or close this panel and try again shortly.")));
      showError(error.reservationRoutingUnconfirmed
        ? 'Your earlier hold has ended, but your cart still needs updating. Select Retry check below, or close this panel and try again shortly.'
        : (error.reservationReselect
          ? 'Your earlier hold has ended. Choose a time for your current cart.'
          : (error.reservationReleaseRequired
            ? 'Your cart changed. Select Release old time and choose again below, or close this panel to keep the earlier hold.'
            : (recovery.releasePendingFor
              ? "We couldn't confirm the earlier hold was released. Select Retry check below, or close this panel and try again shortly."
              : (recovery.cartId !== getCartId()
                ? 'Your cart changed. Check its reservation below before choosing another time, or close this panel and try again shortly.'
                : ordinaryFailure)))));
      lockCheckout();
      updateFooter();
    }).finally(function () {
      finished = true;
      clearTimeout(cartWatchdog);
      if (state.reservationOperation === epoch) {
        state.reservationPending = false;
        if (keepCurrentBtn) keepCurrentBtn.disabled = false;
      }
    });
  }

  function finishReservationFlow() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var path = window.location.pathname || '';
    var alreadyOnCart = path === cartUrl || /(^|\/)cart\/?$/.test(path);
    if (!state.returnToCart || alreadyOnCart) {
      // Only a cart control asks to continue to /cart. Header/store journeys
      // close where they started, regardless of cart contents or drawer state.
      // An existing cart page refreshes its shared UI without navigating again.
      close({ closeStorePicker: storePickerActive, force: true });
      if (typeof window.cartDeliveryDetails === 'function') {
        window.cartDeliveryDetails();
      }
      if (window.agileCart && typeof window.agileCart.fetchCart === 'function') {
        window.agileCart.fetchCart();
      }
      return;
    }

    // Stamp cart attributes before unload so /cart doesn't open with a
    // reservation cookie but stale attributes. keepalive survives navigation;
    // backgroundEnrichAndSync may still be cancelled mid-flight.
    try {
      if (typeof getDeliveryAttributesFromCookies === 'function') {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          keepalive: true,
          body: JSON.stringify({ attributes: getDeliveryAttributesFromCookies() })
        }).catch(function () { /* ignore — cart page / backfill heal */ });
      }
    } catch (e) { /* ignore */ }

    window.location.href = cartUrl;
  }

  // Synchronous cookie + localStorage write after a successful reserve -
  // MUST complete before finishReservationFlow() may navigate, since it's
  // what unlocks the cart UI (delivery badge, checkout gating, etc.) on the
  // next page or immediately on the current StorePicker page.
  // Returns the bits backgroundEnrichAndSync() needs so it doesn't have to
  // re-derive them from state (which the shopper could mutate by reopening
  // the overlay before the background work finishes).
  function applyReservationCookies(data) {
    if (typeof setCookie !== 'function') return null;

    var response = (data && data.response) || {};
    var slot = state.slot;
    var mode = state.mode;
    var day = state.day;
    var storeFromSlot = storeFromUniqueWindow(slot.id) || currentPickingStore();

    // TEMPORARY default: 15-minute hold when the API doesn't tell us
    // seconds_remaining, so the cart at least shows a real expiry.
    var secondsRemaining = response.seconds_remaining || (15 * 60);
    var expiryTime = new Date().getTime() + secondsRemaining * 1000;

    setCookie('delivery_expires_time', expiryTime, 365);
    setCookie('delivery_date', day, 365);
    setCookie('delivery_type', mode, 365);
    setCookie('delivery_unique_window', slot.id, 365);
    setCookie('delivery_time', slot.value || slot.description || '', 365);
    setCookie('delivery_time_short', slot.description || '', 365);
    setCookie('picking_store', String(storeFromSlot), 365);
    writeStoredModeIntent(mode);

    if (mode === 'M') {
      setCookie('delivery_cc_store', String(storeFromSlot), 365);
      try { localStorage.setItem('clickCollectSelected', 'true'); } catch (e) { /* ignore */ }
      document.body.classList.add('click-collect-active');
    } else {
      // Express must not keep a stale C&C store from a prior reservation.
      if (typeof removeCookie === 'function') removeCookie('delivery_cc_store');
      try { localStorage.removeItem('clickCollectSelected'); } catch (e) { /* ignore */ }
      document.body.classList.remove('click-collect-active');
    }

    // Prefer courier/run from the reserve response when Azure returns them.
    // When either value is absent, clear the previous reservation's value now;
    // backgroundEnrichAndSync will fetch the exact store-specific contract.
    // (C&C naming is not uniform enough to derive safely in the theme.)
    var courier = response.courier || response.delivery_courier || '';
    var run = response.delivery_run || '';
    if (courier) setCookie('delivery_courier', courier, 365);
    else if (typeof removeCookie === 'function') removeCookie('delivery_courier');
    if (run) setCookie('delivery_run', run, 365);
    else if (typeof removeCookie === 'function') removeCookie('delivery_run');

    try {
      localStorage.setItem('currentPickingStore', String(storeFromSlot));
      localStorage.setItem('selectedLocation', String(storeFromSlot));
      localStorage.setItem('activeDelivery', 'true');
      localStorage.setItem('hf_picking_store_chosen', '1');
    } catch (e) { /* ignore */ }

    // Quiet write only — do NOT call locationManager.setLocation() here.
    // setLocation() dispatches locationChanged, and collection/search pages
    // listen for that by rewriting the URL (available_locations filter) which
    // reloads the current page and can steal finishReservationFlow's navigation.
    // Persist via the browsing writer (no event), then stamp activeDelivery.
    if (window.locationManager) {
      if (typeof window.locationManager.persistBrowsingLocation === 'function') {
        window.locationManager.persistBrowsingLocation(String(storeFromSlot));
      } else if (window.locationManager.currentLocation !== undefined) {
        window.locationManager.currentLocation = String(storeFromSlot);
      }
      try { localStorage.setItem('activeDelivery', 'true'); } catch (e) { /* ignore */ }
    }

    var postcode = (typeof getCookie === 'function' && getCookie('postcode')) || '';
    var needsCourierBackfill = !String(courier || '').trim() || !String(run || '').trim();
    var expressFlag = mode === 'EX' ? 'true' : 'false';

    return {
      slot: slot,
      day: day,
      storeFromSlot: storeFromSlot,
      postcode: postcode,
      needsCourierBackfill: needsCourierBackfill,
      expressFlag: expressFlag
    };
  }

  // Courier/run enrichment + cart-attribute sync after a reserve. Regular
  // reserve flows deliberately ignore the returned promise and navigate from
  // the synchronous cookie write. Transactional cart replacements await it so
  // their completion state includes the final enriched attribute sync.
  // Mirrors the legacy drawer contract (FLOW-ORDER-ATTRIBUTE-GUARD.md):
  // courier + delivery_run must land for Express or checkout stays blocked
  // (cart.js isDeliveryEnrichmentMissing). Azure's set response may not
  // carry them yet — fall back to postcodedetails, then scheduleCourierBackfill.
  function backgroundEnrichAndSync(data, cookieInfo, options) {
    if (!cookieInfo) return;
    var slot = cookieInfo.slot;
    var day = cookieInfo.day;
    var storeFromSlot = cookieInfo.storeFromSlot;
    var postcode = cookieInfo.postcode;
    var needsCourierBackfill = cookieInfo.needsCourierBackfill;
    var expressFlag = cookieInfo.expressFlag;

    function syncAttrs() {
      if (typeof syncDeliveryAttributesToCart === 'function') {
        return syncDeliveryAttributesToCart().catch(function (err) {
          console.warn('CartSlotOverlay: cart attribute sync failed after reserve:', err);
          // A transactional replacement cannot finish until this write succeeds.
          if (options && options.requireSync) throw err;
        });
      }
      return Promise.resolve();
    }

    // Immediate postcodedetails fill so checkout isn't blocked for ~25s
    // waiting on scheduleCourierBackfill alone. Runs in the background now -
    // finishReservationFlow() already ran, so this no longer holds up the UI.
    var enrichPromise = Promise.resolve();
    if (needsCourierBackfill && postcode && typeof fetchPostcodeDetailsWithRetry === 'function') {
      enrichPromise = fetchPostcodeDetailsWithRetry(postcode, day, expressFlag, 2)
        .then(function (result) {
          if (typeof getCookie === 'function' && (getCookie('delivery_unique_window') !== slot.id
            || getCookie('postcode') !== String(postcode))) return;
          if (!result || !result.response || !result.response[0]) return;
          var row = result.response[0];
          var rowStore = row.picking_store ? String(row.picking_store) : null;
          if (storeFromSlot && rowStore && rowStore !== String(storeFromSlot)) {
            console.warn(
              'CartSlotOverlay: postcodedetails store ' + rowStore +
              ' disagrees with reserved slot store ' + storeFromSlot + ' — not stamping courier/run'
            );
            return;
          }
          if (row.courier) setCookie('delivery_courier', row.courier, 365);
          if (row.delivery_run != null) setCookie('delivery_run', row.delivery_run, 365);
        })
        .catch(function (err) {
          console.warn('CartSlotOverlay: immediate courier lookup failed:', err);
        });
    }

    // Belt-and-braces delayed heal (same helper the legacy drawer uses).
    if (needsCourierBackfill && postcode && typeof scheduleCourierBackfill === 'function') {
      scheduleCourierBackfill(postcode, slot.id, day, expressFlag, String(storeFromSlot));
    }

    // Best-effort: if we already navigated via window.location.href, the
    // page may unload before this POST completes. syncDeliveryAttributesToCart
    // doesn't take a keepalive option (shared with the legacy drawer flow -
    // not modified here), but cookies written synchronously above already
    // unlock the cart UI, and short same-origin POSTs commonly complete
    // even across an unload, so this is still worth firing rather than
    // skipping outright.
    return enrichPromise.then(syncAttrs, syncAttrs);
  }

  // ---------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------

  function openStorePicker() {
    if (state.reservationPending) return;
    close({ restoreStorePickerFocus: false });
    if (window.StorePicker && typeof window.StorePicker.open === 'function') {
      var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      var path = window.location.pathname || '';
      var onCartPage = path === cartUrl || /(^|\/)cart\/?$/.test(path);
      window.StorePicker.open(onCartPage
        ? { requireSlot: true, source: 'cart-page', returnToCart: state.returnToCart }
        : { returnToCart: state.returnToCart });
    }
  }

  function bindEvents() {
    if (backBtn) backBtn.addEventListener('click', function (event) { event.preventDefault(); close(); });
    if (closeBtn) closeBtn.addEventListener('click', function (event) {
      event.preventDefault();
      close({ closeStorePicker: storePickerActive });
    });
    if (editBtn) editBtn.addEventListener('click', function (event) { event.preventDefault(); openStorePicker(); });
    if (openPickerBtn) openPickerBtn.addEventListener('click', function (event) { event.preventDefault(); openStorePicker(); });
    if (reserveBtn) reserveBtn.addEventListener('click', function (event) { event.preventDefault(); reserveSelectedSlot(); });
    if (keepCurrentBtn) keepCurrentBtn.addEventListener('click', function (event) {
      event.preventDefault();
      if (state.reservationPending) return;
      close({ closeStorePicker: storePickerActive, force: true });
    });

    bindModeEvents();
    bindPeriodEvents();

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && overlay && !overlay.hidden) {
        event.stopPropagation();
        close();
      }
    });

    // After Edit -> StorePicker, refresh (and re-fetch if the store/postcode
    // changed) so we never show slots for a stale store after confirmation.
    document.addEventListener('hfPickingStoreChanged', function () {
      // Drop the previous store's cache. Fetch only if times are on screen.
      invalidateSlotRequest();
      if (state.reservationPending) state.reservationEpoch++;
      state.payload = null;
      state.fetchedFor = null;
      if (overlay && !overlay.hidden) {
        open({ returnToCart: state.returnToCart });
      }
    });

    // Closing the drawer must close Select a time — otherwise reopening
    // via the header cart icon resurrects the leftover .is-open overlay on
    // top of the cart. A fresh open always shows cart contents; View cart
    // (while the drawer is already open) is what re-opens the overlay.
    var cartDrawer = document.querySelector('.js-go-cart-drawer');
    if (cartDrawer && typeof MutationObserver === 'function') {
      var wasOpen = cartDrawer.classList.contains('is-open');
      new MutationObserver(function () {
        var isOpen = cartDrawer.classList.contains('is-open');
        if (!isOpen && wasOpen) {
          close();
        } else if (isOpen && !wasOpen) {
          // Header cart icon / ATC open — never resume a leftover Select a
          // time panel (including standalone from the cart page Edit path).
          if (overlay && !overlay.hidden) close();
        }
        wasOpen = isOpen;
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function init() {
    if (!cacheElements()) {
      console.warn('CartSlotOverlay: #cart-slot-overlay not found on this page - skipping init.');
      return;
    }
    bindEvents();
    // Do not fetch on page load, even for returning shoppers. open() loads
    // slots when the shopper asks to choose a time.
  }

  window.CartSlotOverlay = {
    open: open,
    close: close,
    prefetch: prefetch,
    isWithinStorePicker: function () { return storePickerActive; },
    releaseForBrowse: releaseForBrowse,
    isReservationUnsettled: isReservationUnsettled
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
